import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSEO } from "../hooks/useSEO";
import { useReveal } from "../hooks/useReveal";

const PALETTE = [
  { name: "Noir principal", hex: "#0A0908", usage: "Background principal" },
  { name: "Noir profond", hex: "#000000", usage: "Héros, sections pleine page" },
  { name: "Ivoire", hex: "#F5F1E8", usage: "Texte principal" },
  { name: "Or vieilli", hex: "#C9A961", usage: "Accents, liens, titres en or" },
  { name: "Or profond", hex: "#8B6F2B", usage: "États hover" },
  { name: "Champagne", hex: "#E4D4A8", usage: "Highlights subtils" },
  { name: "Gris carte", hex: "#2A2724", usage: "Fonds de cartes, séparateurs" },
  { name: "Bordeaux", hex: "#4A1C2B", usage: "Optionnel — accents secondaires" },
];

const TYPOGRAPHY = [
  { name: "Cormorant Garamond", role: "Titres, citations, éditorial", weight: "700 — italic" },
  { name: "Inter", role: "Labels, navigation, UI", weight: "400, 500, 600" },
];

function ColorSwatch({ color }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(color.hex);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={copy}
      className="group flex flex-col text-left"
      style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }}
      aria-label={`Copier ${color.hex}`}
    >
      <div
        style={{
          width: "100%",
          aspectRatio: "1 / 1",
          background: color.hex,
          border: color.hex === "#000000" ? "1px solid #1c1c1c" : "none",
          transition: "transform 300ms ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(0.97)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
      />
      <p className="label mt-4" style={{ color: "#F8F6F0", fontSize: 11 }}>{color.name}</p>
      <p className="label mt-1" style={{ color: copied ? "#C9A961" : "#A1A1AA", fontSize: 11 }}>
        {copied ? "Copié !" : color.hex}
      </p>
      <p className="mt-1" style={{ color: "rgba(248,246,240,0.35)", fontSize: 11, lineHeight: 1.4 }}>{color.usage}</p>
    </button>
  );
}

function RequestForm() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", media: "", message: "", website: "" });
  const [status, setStatus] = useState({ kind: "idle", message: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ kind: "loading", message: "" });
    try {
      await api.post("/press-kit/request", form);
      setStatus({ kind: "ok", message: "Demande envoyée. Vous recevrez le dossier dans les 48 heures." });
    } catch (err) {
      setStatus({ kind: "error", message: formatApiErrorDetail(err.response?.data?.detail) || "Une erreur est survenue." });
    }
  };

  if (status.kind === "ok") {
    return (
      <p className="label" style={{ color: "#C9A961", border: "1px solid rgba(201,169,97,0.3)", padding: "20px 24px" }}>
        {status.message}
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="press-kit-form">
      <input type="text" name="website" value={form.website} onChange={set("website")} style={{ display: "none" }} tabIndex={-1} aria-hidden="true" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="label block mb-2" style={{ color: "#A1A1AA" }}>Prénom *</label>
          <input required value={form.first_name} onChange={set("first_name")} className="input-editorial" style={{ width: "100%" }} placeholder="Marie" />
        </div>
        <div>
          <label className="label block mb-2" style={{ color: "#A1A1AA" }}>Nom *</label>
          <input required value={form.last_name} onChange={set("last_name")} className="input-editorial" style={{ width: "100%" }} placeholder="Dupont" />
        </div>
      </div>
      <div>
        <label className="label block mb-2" style={{ color: "#A1A1AA" }}>Adresse e-mail *</label>
        <input required type="email" value={form.email} onChange={set("email")} className="input-editorial" style={{ width: "100%" }} placeholder="m.dupont@lemonde.fr" />
      </div>
      <div>
        <label className="label block mb-2" style={{ color: "#A1A1AA" }}>Média / Publication *</label>
        <input required value={form.media} onChange={set("media")} className="input-editorial" style={{ width: "100%" }} placeholder="Le Monde, France Inter, Konbini…" />
      </div>
      <div>
        <label className="label block mb-2" style={{ color: "#A1A1AA" }}>Message (optionnel)</label>
        <textarea
          value={form.message}
          onChange={set("message")}
          className="input-editorial"
          style={{ width: "100%", minHeight: 100, resize: "vertical" }}
          placeholder="Précisez l'objet de votre demande si nécessaire."
        />
      </div>
      {status.kind === "error" && (
        <p className="label" style={{ color: "#e05c5c" }}>{status.message}</p>
      )}
      <button type="submit" className="btn-solid" style={{ width: "100%" }} disabled={status.kind === "loading"}>
        {status.kind === "loading" ? "Envoi…" : "Demander le dossier de presse →"}
      </button>
    </form>
  );
}

export default function PressKit() {
  useSEO("Kit presse", "Palette de couleurs, typographie et demande de dossier de presse officiel du Stand d'Or.");

  const paletteRef = useReveal();
  const typoRef = useReveal();
  const formRef = useReveal();

  return (
    <div data-testid="press-kit-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden grain"
        style={{ background: "#000", minHeight: "50vh", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(0,0,0,0.05) 0%,rgba(0,0,0,0.6) 60%,#000 100%)" }} aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pb-20 pt-[180px] w-full">
          <Link to="/presse" className="label manifeste-fade" style={{ color: "#A1A1AA", display: "inline-block", marginBottom: 40 }}>
            ← Salle de presse
          </Link>
          <p className="label manifeste-fade" style={{ color: "#C9A961", marginBottom: 20 }}>Identité visuelle</p>
          <h1
            className="font-serif manifeste-title"
            style={{ fontSize: "clamp(52px, 9vw, 140px)", lineHeight: 0.92, letterSpacing: "-0.03em", color: "#F8F6F0" }}
          >
            Kit presse
          </h1>
        </div>
      </section>

      {/* INTRO */}
      <div style={{ borderTop: "1px solid #1c1c1c", borderBottom: "1px solid #1c1c1c", background: "#050505" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
          <p style={{ color: "rgba(248,246,240,0.65)", fontSize: 16, lineHeight: 1.75, maxWidth: 680 }}>
            Retrouvez ici les éléments d'identité visuelle du Stand d'Or — palette officielle, typographie — ainsi
            que le formulaire de demande du dossier de presse complet (biographies du jury, visuels HD, charte graphique).
          </p>
        </div>
      </div>

      {/* PALETTE */}
      <section
        ref={paletteRef}
        className="reveal max-w-[1400px] mx-auto px-6 lg:px-12 py-24 md:py-32"
        data-testid="press-kit-palette"
      >
        <p className="label mb-6" style={{ color: "#C9A961" }}>I. Palette officielle</p>
        <h2 className="font-serif mb-16" style={{ fontSize: "clamp(28px, 3.5vw, 48px)", color: "#F8F6F0", lineHeight: 1.05 }}>
          Huit couleurs. Une identité.
        </h2>
        <p className="mb-12" style={{ color: "rgba(248,246,240,0.55)", fontSize: 15, lineHeight: 1.7, maxWidth: 560 }}>
          Cliquez sur une couleur pour copier son code hexadécimal.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6">
          {PALETTE.map((c) => <ColorSwatch key={c.hex} color={c} />)}
        </div>
      </section>

      <div className="hairline max-w-[1400px] mx-auto" />

      {/* TYPOGRAPHIE */}
      <section
        ref={typoRef}
        className="reveal max-w-[1400px] mx-auto px-6 lg:px-12 py-24 md:py-32"
        data-testid="press-kit-typo"
      >
        <p className="label mb-6" style={{ color: "#C9A961" }}>II. Typographie</p>
        <h2 className="font-serif mb-16" style={{ fontSize: "clamp(28px, 3.5vw, 48px)", color: "#F8F6F0", lineHeight: 1.05 }}>
          Deux familles. Une hiérarchie.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {TYPOGRAPHY.map((t) => (
            <div key={t.name} style={{ borderTop: "1px solid #1c1c1c", paddingTop: 32 }}>
              <p className="label mb-4" style={{ color: "#A1A1AA" }}>{t.role}</p>
              <p
                className="font-serif"
                style={{ fontSize: "clamp(36px, 5vw, 64px)", color: "#F8F6F0", lineHeight: 1, letterSpacing: "-0.02em" }}
              >
                {t.name}
              </p>
              <p className="label mt-4" style={{ color: "#A1A1AA" }}>{t.weight}</p>
              {t.name === "Cormorant Garamond" && (
                <p className="font-serif italic mt-6" style={{ fontSize: 22, color: "rgba(248,246,240,0.55)", lineHeight: 1.4 }}>
                  « L'humour entre dans sa légende. »
                </p>
              )}
              {t.name === "Inter" && (
                <p className="label mt-6" style={{ color: "rgba(248,246,240,0.55)", letterSpacing: "0.12em" }}>
                  ÉDITION MMXXVI — LE STAND D'OR
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      <div className="hairline max-w-[1400px] mx-auto" />

      {/* FORMULAIRE */}
      <section
        ref={formRef}
        className="reveal max-w-[1400px] mx-auto px-6 lg:px-12 py-24 md:py-32"
        data-testid="press-kit-form-section"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-4">
            <p className="label mb-6" style={{ color: "#C9A961" }}>III. Dossier complet</p>
            <h2 className="font-serif mb-8" style={{ fontSize: "clamp(28px, 3.5vw, 48px)", color: "#F8F6F0", lineHeight: 1.05 }}>
              Demander le dossier de presse.
            </h2>
            <p style={{ color: "rgba(248,246,240,0.6)", fontSize: 16, lineHeight: 1.75 }}>
              Le dossier complet inclut les visuels haute définition, les biographies du jury, la charte graphique
              complète, le plan de cérémonie et les éléments de langage officiels.
            </p>
            <p className="label mt-8" style={{ color: "#A1A1AA" }}>
              Réponse sous 48 h ouvrées.
            </p>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <RequestForm />
          </div>
        </div>
      </section>
    </div>
  );
}
